import { Icon28InfoCircleOutline } from '@vkontakte/icons';
import { Spacing, Text, Title } from '@vkontakte/vkui';
import React, { memo, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fullTariff, trialTariff } from 'reactApp/appHelpers/featuresHelpers/features/publicDownloadLimits';
import { loadPaidInfoRequest } from 'reactApp/modules/paidInfo/paidInfo.module';
import { PaidInfoSelectors } from 'reactApp/modules/paidInfo/paidInfo.selectors';
import { loadProductsAndOpenFastCheckout } from 'reactApp/modules/payment/payment.module';
import { openPopupHelper } from 'reactApp/modules/popup/popup.helpers';
import { popupNames } from 'reactApp/modules/popup/popup.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';
import { sendDwh } from 'reactApp/utils/ga';

import styles from './DownloadLimitsTariffModal.css';

export type DownloadLimitsTariffModalProps = {
    onClose?: () => void;
};

const sendAnalytics = (action) => {
    sendDwh({ eventCategory: 'splashscreen-alert', action, dwhData: { name: 'mnz_limit_sharing' } });
};

export const DownloadLimitsTariffModal = memo<DownloadLimitsTariffModalProps>(({ onClose }) => {
    const dispatch = useDispatch();
    const email = useSelector(UserSelectors.getEmail);
    const scrollRef = useRef<HTMLDivElement>(null);
    const isUserWithoutPayment = useSelector(PaidInfoSelectors.isUserWithoutPayment);
    const productId = isUserWithoutPayment ? trialTariff : fullTariff;

    useEffect(() => {
        dispatch(loadPaidInfoRequest());
        sendAnalytics('show');
    }, [dispatch]);

    const handleClose = useCallback(() => {
        sendAnalytics('close');
        onClose?.();
    }, [onClose]);

    const handleClickBye = useCallback(() => {
        sendAnalytics('click');
        dispatch(
            loadProductsAndOpenFastCheckout({
                productId,
                paySource: 'limit_sharing_touch_splash',
                title: 'Подписка Mail Space: скачивания без лимита и другие опции',
                hideOtherTariffs: true,
            })
        );
    }, [productId]);

    const handleClickAuth = () => {
        const path = window.location.pathname;
        const successPage = `${window.location.origin}${path}`;

        window.__PH.authForm.show({
            formType: 'login',
            successPage,
            email,
        });

        window.__PH?.authForm.show({ source: 'cloud-public-mob', from: 'navi' });
    };

    return (
        <MobileDialog
            id="mobile-download-limits-tariff"
            onClose={handleClose}
            closeOnDimmerClick
            topmost
            mod="base"
            contextPadding={8}
            scrollRef={scrollRef}
        >
            <div ref={scrollRef} className={styles.block}>
                <Icon28InfoCircleOutline width={48} height={48} className={styles.iconInfo} />
                <Spacing size={24} />
                <Title level="1">Достигнут лимит на&nbsp;скачивание</Title>
                <Spacing size={8} />
                <Text>
                    Снять его можно с&nbsp;подпиской&nbsp;&mdash; <br />
                    расскажите владельцу или оформите для&nbsp;себя
                </Text>
                <Spacing size={24} />
                {email ? (
                    <Button theme="vk" sizeMode={ButtonSizeMode.big} onClick={handleClickBye} primary>
                        {isUserWithoutPayment ? 'Снять лимит за 1 ₽' : 'Снять лимит'}
                    </Button>
                ) : (
                    <Button theme="vk" sizeMode={ButtonSizeMode.big} onClick={handleClickAuth} primary>
                        Авторизоваться
                    </Button>
                )}
            </div>
        </MobileDialog>
    );
});

export const renderDownloadLimitsTariffModal = (data?: DownloadLimitsTariffModalProps) => {
    sendDwh({ eventCategory: 'viewer', action: 'no_download_click' });
    openPopupHelper({ popupName: popupNames.DOWNLOAD_LIMITS, data });
};

DownloadLimitsTariffModal.displayName = 'DownloadLimitsTariffModal';
